
import { uniqueId } from "lodash";
import Vue from "vue";
import Component from 'vue-class-component';
import PrintFormat from "./PrintFormat.vue";
import { PrintFormat as PrintFormatModel } from '@/model/PrintFormat';
import { requiredRule } from "@/helpers/validation/requiredRule";
import PriceInput from "./PriceInput.vue";
import { Prop, Watch } from "vue-property-decorator";
import SavedPricelists from '../SavedPricelists.vue';
import { PriceList, priceListEquals } from "@/model/PriceList";
import store from "@/store";
import Product from "./Product.vue";
import { PriceListProduct } from "@/model/PriceListProduct";
import ProductsDialog from "./ProductsDialog.vue";
import RegularContainer from "./inputs/containers/RegularContainer.vue";
import SwitchedPriced from "./inputs/SwitchedPriced.vue";
import RegularPriced from "./inputs/RegularPriced.vue";
import Switched from "./inputs/Switched.vue";
import api from "@/service/api";
import { getModule } from "vuex-module-decorators";
import PriceListsModule from "@/store/modules/PriceLists";
import ConfirmCancel from './ConfirmCancel.vue';
import ConfirmationModel from '@/components/ConfirmationModel';

const priceListsModule = getModule(PriceListsModule, store);

type SwitchedPricedValue = {
    enabled: boolean;
    price: number | null;
};

function switchedToModel(switched: SwitchedPricedValue): { enabled: true; price: number; }|{ enabled: false } {
    return !switched.enabled ? { enabled: false } : {
        enabled: true,
        price: switched.price as number,
    };
}

function modelToSwitched(model: { enabled: true; price: number; }|{ enabled: false }): SwitchedPricedValue {
    return !model.enabled ? { enabled: false, price: null } : {
        enabled: true,
        price: model.price as number
    };
}

@Component({
    components: {
        PrintFormat,
        PriceInput,
        SavedPricelists,
        Product,
        ProductsDialog,
        Switched,
        SwitchedPriced,
        RegularPriced,
        RegularContainer,
        ConfirmCancel
    }
})
export default class PriceListForm extends Vue {
    products: Array<{ _id: any, model: PriceListProduct }> = [];
    @Prop({
        type: Object,
        required: false
    })
    value: PriceList|null = null;
    @Prop({
        type: Boolean,
        default: false
    })
    loading!: boolean;
    selectedTab: number = 0;
    currentTab: number = 0;
    photosInPacket: number | null = null;
    preventDialogClose = false;
    public isSellingPackets = false;
    public isSellingOverPacket = true;
    public printSaleDisabled = false;
    public printBudget: SwitchedPricedValue = {
        enabled: false,
        price: null,
    };
    public digitalDebited = false;

    nonZeroOrder = true;
    mustPickPrints = false;

    name: string = '';
    photosInPacketRules = [ 
        requiredRule,
        (val: string) => Number.isInteger(Number(val)) || 'Wartość musi być liczbą całkowitą',
        (val: string) => Number.parseInt(val) > 0 || 'Wartość musi być większa od 0',
    ];
    nameRules = [ requiredRule, (value: string) => value.trim().length <= 64 || 'Maks. 64 znaki' ];

    formats: Array<{ _id: string; model: (PrintFormatModel | null ) }> = [];

    digital: SwitchedPricedValue = {
        enabled: false,
        price: null,
    };
    packetPrice: SwitchedPricedValue = {
        enabled: false,
        price: null,
    };
    extraPhotoPrice: number | null = null;
    photoBasePrice: SwitchedPricedValue = {
        enabled: false,
        price: null,
    };

    applyLowestWhenDigital = false;
    savingInProgress = false;
    submitAttempted = false;
    cancelConfirmationModel: ConfirmationModel = new ConfirmationModel();
    public exitRunBlink = false;
    private exitRunBlinkTimeout: number | null = null;
    private formatsColumn: HTMLElement | null = null;
    private formatToScrollId: string | null = null;
    private formatsWheelRemainder = 0;
    private readonly formatsWheelOptions: AddEventListenerOptions = { capture: true, passive: false };
    private readonly formatsWheelHandler = (event: WheelEvent) => this.onFormatsWheel(event);

    mounted() {
        store.subscribeAction({
            after: (action) => {
                if (action.type === 'deletePriceList' && action.payload === this.value?.id) {
                    this.$emit('input', null);
                }
            }
        });
        this.$nextTick(() => this.bindFormatsWheel());
    }

    beforeDestroy() {
        this.unbindFormatsWheel();
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }
    }

    get currentValue() {
        return <PriceList>{
            id: this.value?.id,
            name: this.name.trim(),
            digitalPrice: switchedToModel(this.digital),
            photoBasePrice: switchedToModel(this.isSellingPackets ? { enabled: false, price: null } : this.photoBasePrice),
            mustPickPrints: !this.printSaleDisabled && this.mustPickPrints,
            nonZeroOrder: this.nonZeroOrder,
            printSaleDisabled: this.printSaleDisabled,
            applyLowestWhenDigital: !this.printSaleDisabled && this.digital.enabled && this.applyLowestWhenDigital,
            isSellingPackets: this.isSellingPackets,
            photosInPacket: this.isSellingPackets ? Number(this.photosInPacket) : null,
            packetPrice: switchedToModel(this.isSellingPackets ? this.packetPrice : { enabled: false, price: null }),
            isSellingOverPacket: this.isSellingPackets && this.isSellingOverPacket,
            extraPhotoPrice: this.isSellingPackets && this.isSellingOverPacket ? this.extraPhotoPrice : null,
            printBudget: switchedToModel(this.isSellingPackets && !this.printSaleDisabled ? this.printBudget : { enabled: false, price: null }),
            digitalDebited: this.isSellingPackets && !this.printSaleDisabled && this.digital.enabled && this.printBudget.enabled && this.digitalDebited,
            formats: this.formats.map(({ model }) => model).filter(f => f !== null) as PrintFormatModel[],
            products: this.products.map(({ model }) => model).filter(f => f !== null) as PriceListProduct[],
        };
    }

    get hasPurchasableOption() {
        const hasPrints = !this.printSaleDisabled && this.currentValue.formats.length > 0;
        const hasSingleProduct = this.currentValue.products.length > 0;
        const hasDigitalFiles = this.digital.enabled;

        return hasPrints || hasSingleProduct || hasDigitalFiles;
    }

    get globalValidationErrors() {
        const errors: string[] = [];

        if (!this.hasPurchasableOption) {
            errors.push('Cennik musi zawierać co najmniej jedną opcję zakupu: odbitki, produkt dodatkowy lub zdjęcia cyfrowe.');
        }

        return errors;
    }

    get showGlobalValidationErrors() {
        return this.submitAttempted && this.globalValidationErrors.length > 0;
    }



    get isFormEmpty() {
        return !this.name && 
               this.formats.length === 0 &&
               this.products.length === 0 &&
               !this.photoBasePrice.enabled && 
               !this.digital.enabled &&
               !this.isSellingPackets &&
               !this.packetPrice.enabled &&
               !this.printBudget.enabled &&
               !this.mustPickPrints &&
               !this.digitalDebited &&
               !this.printSaleDisabled &&
               !this.applyLowestWhenDigital &&
               this.nonZeroOrder === true;
    }

    get isDirty() {
        if (!this.value) {
            // For new price lists, check if form has any meaningful data
            return !this.isFormEmpty;
        }
        return !this.currentValue || !priceListEquals(this.value, this.currentValue);
    }

    async close() {
        if (this.isDirty) {
            const save = await this.cancelConfirmationModel.confirm('Zapisać zmiany?');
            if (save) {
                if (!(await this.submit())) return;
            } else {
                // Reset form to original values when user doesn't want to save
                this.valueChanged(this.value);
            }
            this.$emit("close");
        } else {
            this.$emit("close");
        }
    }

    public blinkExitRun() {
        this.exitRunBlink = false;
        if (this.exitRunBlinkTimeout) {
            window.clearTimeout(this.exitRunBlinkTimeout);
            this.exitRunBlinkTimeout = null;
        }

        this.$nextTick(() => {
            this.exitRunBlink = true;
            this.exitRunBlinkTimeout = window.setTimeout(() => {
                this.exitRunBlink = false;
                this.exitRunBlinkTimeout = null;
            }, 900);
        });
    }

    @Watch('value', {
        immediate: true,
    })
    valueChanged(v: PriceList|null) {
        this.submitAttempted = false;
        if (!v) {
            this.name = '';
            this.formats = [];
            this.products = [];

            this.photoBasePrice = { enabled: false, price: null };
            this.digital = { enabled: false, price: null };

            this.isSellingPackets = false;
            this.photosInPacket = null;
            this.packetPrice = { enabled: false, price: null };
            this.isSellingOverPacket = false;
            this.extraPhotoPrice = null;
            this.printBudget = { enabled: false, price: null };
            this.mustPickPrints = false;
            this.digitalDebited = false;
            this.printSaleDisabled = false;

            this.applyLowestWhenDigital = false;
            this.nonZeroOrder = true;

            if (this.$refs.form) (this.$refs.form as any).resetValidation();
        } else {
            this.name = v.name;
            this.formats = v.formats.map(f => ({
                _id: uniqueId(),
                model: f,
            }));
            this.products = v.products.map(p => ({
                _id: uniqueId(),
                model: p,
            }));

            this.photoBasePrice = modelToSwitched(v.photoBasePrice);
            this.digital = modelToSwitched(v.digitalPrice);
            this.isSellingPackets = v.isSellingPackets;
            this.photosInPacket = v.photosInPacket;
            this.packetPrice = modelToSwitched(v.packetPrice);
            this.isSellingOverPacket = !!v.isSellingOverPacket;
            this.extraPhotoPrice = v.extraPhotoPrice;
            this.printBudget = modelToSwitched(v.printBudget);
            this.mustPickPrints = v.mustPickPrints;
            this.digitalDebited = v.digitalDebited;
            this.printSaleDisabled = v.printSaleDisabled;

            this.applyLowestWhenDigital = v.applyLowestWhenDigital;
            this.nonZeroOrder = v.nonZeroOrder;
        }
    }

    async submit(): Promise<boolean> {
        if (this.savingInProgress) return false;
        this.savingInProgress = true;
        this.submitAttempted = true;

        try {
            if (!(this.$refs.form as any).validate(true)) {
                Vue.nextTick(() => {
                    const errorElement = document.querySelector('.scrollerFormat .error--text') as HTMLElement;
                    if (errorElement) {
                        this.$vuetify.goTo(errorElement, {
                            container: document.querySelector('.scrollerFormat') as HTMLElement,
                        });
                    }
                });
                return false;
            }
            if (this.globalValidationErrors.length > 0) {
                Vue.nextTick(() => {
                    if (this.$refs.globalErrors) {
                        this.$vuetify.goTo(this.$refs.globalErrors as HTMLElement);
                    }
                });
                return false;
            }

            const priceList = this.currentValue;
            const { priceList: savedPriceList, photoSettingsChangedCount } = priceList.id
                ? await api.updatePriceList(priceList)
                : { priceList: await api.createPriceList(priceList), photoSettingsChangedCount: 0 };
            this.$emit('input', savedPriceList);
            (this as any).$vToastify.success({
                title: 'Zaktualizowano',
                body: 'Zmiany zostały zapisane',
            });
            if (photoSettingsChangedCount > 0) {
                (this as any).$vToastify.warning({
                    title: 'Ustawienia zdjęć zmienione',
                    body: `Niektóre zdjęcia utraciły niezgodne ustawienia (${photoSettingsChangedCount}).`,
                });
            }
            priceListsModule.fetchPriceLists().catch(() => undefined);
            this.submitAttempted = false;
            return true;
        } catch (error) {
            (this as any).$vToastify.error({ title: 'Nie zapisano cennika', body: 'Sprawdź połączenie i spróbuj ponownie.' });
            return false;
        } finally {
            this.savingInProgress = false;
        }
    }

    addFormat() {
        const id = uniqueId();
        this.formatToScrollId = id;
        this.formats.push({ _id: id, model: null });
        Vue.nextTick(() => this.bindFormatsWheel());
    }

    scrollToAddedFormat(element: HTMLElement) {
        if (element.dataset.formatId !== this.formatToScrollId) return;
        this.formatToScrollId = null;

        const container = this.$el.querySelector('.scrollerFormat') as HTMLElement | null;
        if (!container) return;
        const format = element.getBoundingClientRect();
        const visible = container.getBoundingClientRect();
        if (format.top < visible.top || format.bottom > visible.bottom) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    deleteFormat(index: number) {
        Vue.delete(this.formats, index);
    }

    private bindFormatsWheel() {
        const column = this.$el.querySelector('.formats-column') as HTMLElement | null;
        if (this.formatsColumn === column) return;

        this.unbindFormatsWheel();
        this.formatsColumn = column;
        this.formatsColumn?.addEventListener('wheel', this.formatsWheelHandler, this.formatsWheelOptions);
    }

    private unbindFormatsWheel() {
        this.formatsColumn?.removeEventListener('wheel', this.formatsWheelHandler, this.formatsWheelOptions);
        this.formatsColumn = null;
        this.formatsWheelRemainder = 0;
    }

    private onFormatsWheel(event: WheelEvent) {
        const scroller = this.$el.querySelector('.scrollerFormat') as HTMLElement | null;
        if (!scroller || !scroller.contains(event.target as Node)) return;
        if (window.matchMedia('(max-width: 768px)').matches) return;
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

        const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
        if (maxScrollTop <= 0) return;

        const rawDelta = this.normalizeWheelDelta(event, scroller) + this.formatsWheelRemainder;
        const delta = rawDelta < 0 ? Math.ceil(rawDelta) : Math.floor(rawDelta);
        this.formatsWheelRemainder = rawDelta - delta;
        if (delta === 0) {
            event.preventDefault();
            return;
        }

        const nextScrollTop = Math.max(0, Math.min(maxScrollTop, scroller.scrollTop + delta));
        if (nextScrollTop === scroller.scrollTop) return;

        event.preventDefault();
        scroller.scrollTop = nextScrollTop;
    }

    private normalizeWheelDelta(event: WheelEvent, scroller: HTMLElement) {
        if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * 18;
        if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * scroller.clientHeight;
        return event.deltaY;
    }



    @Watch('digital', { deep: true })
    sellsDigitalChanged(v: SwitchedPricedValue) {
        if (!v.enabled) {
            this.digital.price = null;
            this.applyLowestWhenDigital = false;
        }
    }

    @Watch('products', {
        deep: true,
    })
    watchProducts(newVal: any) {
        console.log({ newVal });
    }

    public openList() {
        this.$emit('openList');
    }
}
